import { useCallback, useEffect, useRef, useState } from "react";
import { buildSelector } from "../core/selector";
import { detectComponentName } from "../core/react-name";
import type { TargetInfo } from "../core/types";

interface InspectorProps {
  onSelect: (target: TargetInfo) => void;
  onSkip: () => void;
  onCancel: () => void;
}

interface Hover {
  el: Element;
  rect: DOMRect;
  label: string;
}

function fallbackLabel(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const cls = Array.from(el.classList)
    .filter((c) => !c.startsWith("bw-"))
    .slice(0, 2)
    .join(".");
  return el.id ? `${tag}#${el.id}` : cls ? `${tag}.${cls}` : tag;
}

/**
 * Overlay plein écran de sélection d'élément. Les clics sont interceptés par
 * l'overlay : ils ne déclenchent jamais les actions de la page en dessous.
 */
export function Inspector({ onSelect, onSkip, onCancel }: InspectorProps) {
  const [hover, setHover] = useState<Hover | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const elementAt = useCallback((x: number, y: number): Element | null => {
    const overlay = overlayRef.current;
    if (!overlay) return null;
    overlay.style.pointerEvents = "none";
    const el = document.elementFromPoint(x, y);
    overlay.style.pointerEvents = "auto";
    if (!el || el === document.documentElement || el === document.body) return null;
    if (el.closest("[data-bug-widget]")) return null;
    return el;
  }, []);

  const handleMove = useCallback(
    (e: React.MouseEvent) => {
      const el = elementAt(e.clientX, e.clientY);
      if (!el) {
        setHover(null);
        return;
      }
      setHover((prev) =>
        prev?.el === el
          ? prev
          : { el, rect: el.getBoundingClientRect(), label: detectComponentName(el) ?? fallbackLabel(el) },
      );
    },
    [elementAt],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const el = elementAt(e.clientX, e.clientY);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const componentName = detectComponentName(el);
      onSelect({
        selector: buildSelector(el),
        ...(componentName ? { componentName } : {}),
        rect: { x: rect.x, y: rect.y, width: rect.width, height: rect.height },
      });
    },
    [elementAt, onSelect],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCancel();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onCancel]);

  const labelBelow = hover ? hover.rect.top < 34 : false;

  return (
    <>
      <div
        ref={overlayRef}
        className="bw-inspect-overlay"
        onMouseMove={handleMove}
        onClick={handleClick}
        onMouseLeave={() => setHover(null)}
      />
      {hover && (
        <div
          className="bw-inspect-highlight"
          style={{
            left: hover.rect.left - 2,
            top: hover.rect.top - 2,
            width: hover.rect.width + 4,
            height: hover.rect.height + 4,
          }}
        >
          <span className={"bw-inspect-label" + (labelBelow ? " bw-inspect-label-below" : "")}>
            {hover.label}
          </span>
        </div>
      )}
      <div className="bw-inspect-toolbar">
        <span>Cliquez sur l’élément concerné — Échap pour annuler</span>
        <button type="button" className="bw-link-btn" onClick={onSkip}>
          Annuler
        </button>
      </div>
    </>
  );
}
